import { useState, useEffect, useContext } from "react";
import {
  Box,
  Flex,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  useColorModeValue,
  Stack,
  Image,
  Grid,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverArrow,
  PopoverCloseButton,
  Text,
  Tooltip,
  Avatar,
  WrapItem,
  useBreakpointValue,
  /* ===== ADDED IMPORTS ONLY ===== */
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Select,
} from "@chakra-ui/react";

import ResetPasswordFormModal from "components/Auth/ResetPasswordFormModal";
import { HamburgerIcon, SettingsIcon } from "@chakra-ui/icons";
import { appRequest } from "routes";
import BadgeComponent from "../NotificationNavbar/BadgeComponent";
import BadgeMenuComponent from "../NotificationNavbar/BadgeMenuComponent";
import { AppContext } from "AppProvider/AppContext";
import WorkflowListModal from "App/BsesPoc/components/Dashboard/WorkflowListModal";
import WorkflowListComponent from "components/common/WorkflowListComponent/WorkflowListComponent";
import { RiExchangeFill } from "react-icons/ri";

type setActivePage = React.Dispatch<React.SetStateAction<string>>;

export default function Navbar({
  activePage,
  setActivePage,
  displaySidebar,
  setDisplaySidebar,
  setOpenMobileSideDrawer,
}: {
  activePage: string;
  setActivePage: setActivePage;
  displaySidebar: boolean;
  setOpenMobileSideDrawer: any;
  setDisplaySidebar: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const {
    toastNotify,
    getDefaultWorkFlow,
    workFlowData,
    setActiveWorkflow,
    workflowProcess,
  } = useContext(AppContext);
  const [loading, setLoading] = useState(false)
  /* ===== EXISTING STATE (UNCHANGED) ===== */
  const [isOpen, setIsOpen] = useState(false);
  const [isWorkFlowOpen, setIsWorkFlowOpen] = useState(false);
  const viewerWidth = useBreakpointValue({ lg: true });
  const [active, setActive] = useState(false);

  /* ===== ADDED STATE (ONLY FOR PROCESS MODAL) ===== */
  const [isProcessModalOpen, setIsProcessModalOpen] = useState(false);
  const [selectedProcessName, setSelectedProcessName] = useState(
    workFlowData?.processName || ""
  );
  const [selectedProcessType, setSelectedProcessType] = useState(
    workFlowData?.processType || ""
  );

  const menuBg = useColorModeValue("rgba(255, 255, 255, 0.8)", "rgba(26, 32, 44, 0.8)");
  const menuBorderColor = useColorModeValue("rgba(200, 200, 200, 0.3)", "rgba(255, 255, 255, 0.1)");
  const itemHoverBg = useColorModeValue("rgba(0, 0, 0, 0.04)", "rgba(255, 255, 255, 0.08)");
  const dividerColor = useColorModeValue("rgba(0,0,0,0.06)", "rgba(255,255,255,0.06)");
  const processBtnBg = useColorModeValue("rgba(45, 212, 191, 0.08)", "rgba(45, 212, 191, 0.15)");
  const processBtnBorder = useColorModeValue("rgba(45, 212, 191, 0.2)", "rgba(45, 212, 191, 0.3)");
  const processBtnColor = useColorModeValue("teal.600", "teal.200");

  const modalHeaderBg = useColorModeValue("white", "gray.800");
  const modalHeaderColor = useColorModeValue("gray.700", "white");
  const modalSelectBg = useColorModeValue("gray.100", "gray.700");
  const modalSelectHoverBg = useColorModeValue("gray.200", "gray.600");

  const { accountDetails } = useContext(AppContext);

  const handleOpenModal = () => setIsOpen(true);
  const handleCloseModal = () => setIsOpen(false);

  const handleOpenWorkFlowModal = () => setIsWorkFlowOpen(true);
  const handleCloseWorkFlowModal = () => setIsWorkFlowOpen(false);

  const handleLogout = async () => {
    let processName = workflowProcess?.processName
    try {
      sessionStorage.clear();
      await appRequest("user", "logout");

      toastNotify({
        title: "Logged out",
        message: "You have been logged out successfully",
        type: "success",
      });

      setTimeout(() => {
        window.location.href = processName === "foreign" ? "login?process=foreign" : "/login";
      }, 500);
    } catch {
      window.location.href = processName === "foreign" ? "login?process=foreign" : "/login";
    }
  };

  function getFullName(username: any) {
    if (!username) return "Invalid username";
    const namePart = username.split("@")[0];
    const nameParts = namePart.split(".");
    return nameParts
      .map(
        (part: any) =>
          part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
      )
      .join(" ");
  }

  /* ===== NOTIFICATION LOGIC (UNCHANGED) ===== */
  const [notificationCount, setNotificationCountState] = useState<number>(() => {
     const cached = sessionStorage.getItem("notificationCount");
     return cached ? parseInt(cached) : 0;
  });

  const setNotificationCount = (val: React.SetStateAction<number>) => {
      setNotificationCountState((prev) => {
          const newVal = typeof val === 'function' ? val(prev) : val;
          sessionStorage.setItem("notificationCount", newVal.toString());
          return newVal;
      });
  };

  useEffect(() => {
    const getNotificationCount = async () => {
      if (sessionStorage.getItem("notificationsFetched")) {
          return;
      }
      try {
        const nCount: any = await appRequest(
          "notifications",
          "getunreadcount",
          1
        );
        if (nCount) {
          setNotificationCount(nCount.count);
        } else {
          setNotificationCount(0);
        }
        sessionStorage.setItem("notificationsFetched", "true");
      } catch { }
    };
    if (Object.entries(workFlowData).length > 0) {
      getNotificationCount();
    }
  }, [workFlowData]);

  const activeWorkflowId = sessionStorage.getItem("activeWorkflowId");
  useEffect(() => {
    if (activeWorkflowId) {
      setActiveWorkflow(activeWorkflowId);
    }
  }, [activeWorkflowId, setActiveWorkflow]);

  useEffect(() => {
    if (active === true) {
      getDefaultWorkFlow();
    }
    setActive(false);
  }, [activeWorkflowId]);

  useEffect(() => {
    if (workflowProcess) {
      setSelectedProcessName(workflowProcess?.processName);
      setSelectedProcessType(workflowProcess?.processType);
    }
  }, [workflowProcess]);

  const getLogo = () => {
    if (workFlowData?.processName === "tillomed") return "/img/tillomed.jpg";
    if (workFlowData?.processName === "marcan") return "/img/marcan.jpg";
    if (workFlowData?.processName === "emerging") return "/img/domestic.jpg";
    if (workFlowData?.processName === "zuventus") return "/img/zuventus.jpg";
    if (workFlowData?.processName === "domestic") return "/img/domestic.jpg";
    return "/img/domestic.jpg";
  };

  const handleSetProcess = async () => {
    try {
      setLoading(true);

      const response: any = await appRequest(
        "workflow",
        "setWorkflowProcess",
        {
          processName: selectedProcessName,
          processType: selectedProcessType || "NonPo",
        }
      );

      if (response?.status === "success") {
        sessionStorage.setItem("processName", selectedProcessName);
        sessionStorage.setItem("processType", selectedProcessType);

        toastNotify({
          title: "Success",
          message: response?.data,
          type: "success",
        });

        setLoading(false);

        setTimeout(() => {
          window.location.reload();
        }, 800);

      } else {
        setLoading(false);
        toastNotify({
          title: "Error",
          message: response?.data,
          type: "info",
        });
      }
    } catch (err: any) {
      setLoading(false);
      toastNotify({
        title: "Error",
        message: err?.message,
        type: "error",
      });
    }
  };


  return (
    <>
      {/* ================= NAVBAR (UNCHANGED) ================= */}
      <Flex
        alignItems="center"
        justifyContent="space-between"
        px="6"
        h={{ base: "60px", lg: "80px" }}
        position="sticky"
        w={{ base: "100%", lg: displaySidebar ? "calc(100% - 4rem)" : "100%" }}
        ml={{ base: "unset", lg: displaySidebar ? "4rem" : "0" }}
        zIndex={9}
        top="0px"
        bg={useColorModeValue("gray.1000", "gray.500")}
      >
        <Flex alignItems="center" w={{ lg: "25rem" }}>
          <Box
            display={{ lg: "none" }}
            mr={5}
            fontSize="xl"
            cursor="pointer"
            onClick={() => setOpenMobileSideDrawer(true)}
          >
            <HamburgerIcon />
          </Box>
        </Flex>

        <Grid>
          <Image src={getLogo()} alt="bses-logo" h="50px" />
        </Grid>

        <Flex
          alignItems="center"
          justifyContent="end"
          w={{ lg: "25rem" }}
          gap={4}
        >
          <Box display={{ base: "none", lg: "block" }}>
            <Popover>
              <PopoverTrigger>
                <Box>
                  <Tooltip
                    label={(workflowProcess?.processName === "crm" ? "Marketing" : workflowProcess?.processName) || workFlowData?.flowName || workFlowData?.workFlowName}
                    hasArrow
                    placement="left"
                  >
                    <Box
                      fontWeight="bold"
                      cursor="pointer"
                      _hover={{ textDecoration: "underline" }}
                      onClick={handleOpenWorkFlowModal}
                    >
                      <Text isTruncated maxW="200px" align="end">
                        {(workflowProcess?.processName === "crm" ? "Marketing" : workflowProcess?.processName) || workFlowData?.flowName || workFlowData?.workFlowName}
                      </Text>
                    </Box>
                  </Tooltip>
                </Box>
              </PopoverTrigger>
              <PopoverContent>
                <PopoverArrow />
                <PopoverCloseButton />
                <PopoverHeader fontWeight="bold" textAlign="center">
                  Workflows
                </PopoverHeader>
                <PopoverBody>
                  <WorkflowListComponent onClose={() => null} />
                </PopoverBody>
              </PopoverContent>
            </Popover>
          </Box>

          <Box>
            <Menu gutter={8}>
              <Tooltip label="Current Process" hasArrow placement="bottom">
                <MenuButton
                  as={Button}
                  size="sm"
                  variant="unstyled"
                  display={sessionStorage.getItem("process") === "foreign" ? "none" : "flex"}
                  alignItems="center"
                  justifyContent="center"
                  gap={2}
                  px={4}
                  h="36px"
                  borderRadius="full"
                  bg={processBtnBg}
                  border="1px solid"
                  borderColor={processBtnBorder}
                  color={processBtnColor}
                  fontSize="xs"
                  fontWeight="700"
                  textTransform="uppercase"
                  letterSpacing="wider"
                  backdropFilter="blur(8px)"
                  transition="all 0.3s cubic-bezier(.08,.52,.52,1)"
                  position="relative"
                  overflow="hidden"
                  _before={{
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bg: "linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 50%)",
                    opacity: 0,
                    transition: "opacity 0.3s ease",
                  }}

                  _hover={{
                    bg: "teal.500",
                    color: "white",
                    borderColor: "teal.500",
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 15px rgba(45, 212, 191, 0.4)",
                    _before: { opacity: 1 },
                  }}
                  _active={{
                    transform: "translateY(0)",
                    boxShadow: "0 2px 8px rgba(45, 212, 191, 0.3)",
                  }}
                >
                  <Flex align="center" gap={2}>
                    <RiExchangeFill size="16px" />
                    <Text as="span">{(workFlowData?.processName === "crm" ? "Marketing" : workFlowData?.processName) || "Process"}</Text>
                  </Flex>
                </MenuButton>
              </Tooltip>
              <MenuList
                bg={menuBg}
                backdropFilter="blur(20px) saturate(180%)"
                borderColor={menuBorderColor}
                boxShadow="rgba(0, 0, 0, 0.25) 0px 25px 50px -12px"
                borderRadius="2xl"
                p={4}
                minW="400px"
                zIndex={10}
                border="1px solid"
              >
                <Box px={2} pb={4}>
                  <Text
                    fontSize="10px"
                    fontWeight="800"
                    color="teal.500"
                    textTransform="uppercase"
                    letterSpacing="widest"
                  >
                    Select Workspace
                  </Text>
                </Box>
                <Grid templateColumns={sessionStorage.getItem("process") === "foreign" ? "repeat(1, 1fr)" : "repeat(2, 1fr)"} gap={2}>
                  {[
                    "domestic", "emerging", "marcan", "tillomed", "zuventus",
                    "mmt", "pando", "pr","sr","provakil", "foreign","crm"
                  ].filter(proc => {
                    const sessionProcess = sessionStorage.getItem("process");
                    return sessionProcess === "foreign" ? proc === "foreign" : true;
                  }).map((proc) => {
                    const isActive = workFlowData?.processName === proc;
                    return (
                      <MenuItem
                        key={proc}
                        onClick={() => {
                          setSelectedProcessName(proc);
                          setIsProcessModalOpen(true);
                        }}
                        bg={isActive ? "teal.500" : "transparent"}
                        color={isActive ? "white" : "inherit"}
                        borderRadius="xl"
                        p={3}
                        h="auto"
                        display="flex"
                        flexDirection="column"
                        alignItems="flex-start"
                        transition="all 0.2s cubic-bezier(.08,.52,.52,1)"
                        _hover={{
                          bg: isActive ? "teal.600" : itemHoverBg,
                          transform: "scale(1.02)",
                        }}
                        _active={{ transform: "scale(0.98)" }}
                      >
                        <Flex w="100%" justify="space-between" align="center">
                          <Text
                            fontSize="sm"
                            fontWeight="700"
                            textTransform="capitalize"
                          >
                            {proc==="crm"?"Marketing":proc}
                          </Text>
                          {isActive && (
                            <Box
                              w="6px"
                              h="6px"
                              bg="white"
                              borderRadius="full"
                              boxShadow="0 0 8px rgba(255,255,255,0.8)"
                            />
                          )}
                        </Flex>
                        <Text
                          fontSize="10px"
                          opacity={isActive ? 0.8 : 0.5}
                          fontWeight="600"
                          mt={1}
                        >
                          Workspace
                        </Text>
                      </MenuItem>
                    );
                  })}
                </Grid>
                <MenuDivider my={4} borderColor={dividerColor} />
                <MenuItem
                  onClick={() => setIsProcessModalOpen(true)}
                  fontSize="xs"
                  fontWeight="800"
                  color="teal.500"
                  justifyContent="center"
                  borderRadius="xl"
                  py={2}
                  _hover={{ bg: "teal.50", color: "teal.600" }}
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Global Advanced Settings
                </MenuItem>
              </MenuList>
            </Menu>
          </Box>

          <Menu>
            <BadgeComponent count={notificationCount} />
            <MenuList>
              <BadgeMenuComponent
                setActivePage={setActivePage}
                setNotificationCount={setNotificationCount}
              />
            </MenuList>
          </Menu>

          <Menu>
            <MenuButton as={Button} rounded="full" variant="link">
              <WrapItem>
                <Avatar
                  size="sm"
                  name={accountDetails?.username || ""}
                  bg="teal.400"
                />
              </WrapItem>
            </MenuButton>
            <MenuList alignItems="center">
              <MenuItem fontWeight="bold">
                {accountDetails?.name || getFullName(accountDetails?.username)}
              </MenuItem>

              <MenuDivider />

              <MenuItem onClick={handleOpenModal}>Change Password</MenuItem>

              <MenuDivider />

              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </MenuList>
          </Menu>
        </Flex>

        <ResetPasswordFormModal
          isCurrentPass={true}
          isOpen={isOpen}
          onClose={handleCloseModal}
        />
      </Flex>

      <WorkflowListModal
        isOpen={isWorkFlowOpen}
        onClose={handleCloseWorkFlowModal}
        menu={true}
      />

      {/* ================= PROCESS MODAL (ADDED) ================= */}
      <Modal
        isOpen={isProcessModalOpen}
        onClose={() => setIsProcessModalOpen(false)}
        isCentered
      >
        <ModalOverlay backdropFilter="blur(10px) saturate(180%)" bg="rgba(0, 0, 0, 0.15)" />

        <ModalContent
          borderRadius="2xl"
          overflow="hidden"
          boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.25)"
          border="1px solid"
          borderColor={useColorModeValue("gray.100", "gray.700")}
        >
          <ModalHeader
            bg={modalHeaderBg}
            fontSize="lg"
            fontWeight="700"
            color={modalHeaderColor}
            px={6}
            pt={6}
            pb={4}
          >
            Configure Process
          </ModalHeader>
          <ModalCloseButton top={6} right={6} />

          <ModalBody px={6} py={4}>
            <Stack spacing={6}>
              <Box>
                <Text
                  fontSize="11px"
                  fontWeight="800"
                  color="teal.500"
                  mb={2}
                  textTransform="uppercase"
                  letterSpacing="widest"
                >
                  Process Name
                </Text>
                <Select
                  value={selectedProcessName}
                  onChange={(e) => setSelectedProcessName(e.target.value)}
                  variant="filled"
                  h="45px"
                  borderRadius="xl"
                  bg={modalSelectBg}
                  _hover={{ bg: modalSelectHoverBg }}
                  _focus={{ bg: "white", borderColor: "teal.400" }}
                  fontWeight="600"
                >
                  {sessionStorage.getItem("process") === "foreign" ? (
                    <option value="foreign">foreign</option>
                  ) : (
                    <>
                      <option value="domestic">Domestic</option>
                      <option value="emerging">Emerging</option>
                      <option value="marcan">Marcan</option>
                      <option value="tillomed">Tillomed</option>
                      <option value="zuventus">Zuventus</option>
                      <option value="mmt">MMt</option>
                      <option value="pando">Pando</option>
                      <option value="pr">pr</option>
                                            <option value="sr">sr</option>
                      <option value="provakil">Provakil</option>
                      <option value="foreign">foreign</option>
                                            <option value="crm">Marketing</option>

                    </>
                  )}
                </Select>
              </Box>

              <Box>
                <Text
                  fontSize="11px"
                  fontWeight="800"
                  color="teal.500"
                  mb={2}
                  textTransform="uppercase"
                  letterSpacing="widest"
                >
                  Selection Type
                </Text>
                <Select
                  value={selectedProcessType}
                  onChange={(e) => setSelectedProcessType(e.target.value)}
                  variant="filled"
                  h="45px"
                  borderRadius="xl"
                  bg={modalSelectBg}
                  _hover={{ bg: modalSelectHoverBg }}
                  _focus={{ bg: "white", borderColor: "teal.400" }}
                  fontWeight="600"
                >
                  <option value="NonPo">Non PO</option>
                  {sessionStorage.getItem("process") !== "foreign" && (
                    <option value="Po">PO</option>
                  )}
                </Select>
              </Box>
            </Stack>
          </ModalBody>

          <ModalFooter px={6} pb={6} pt={2}>
            <Button
              variant="ghost"
              mr={3}
              onClick={() => setIsProcessModalOpen(false)}
              borderRadius="xl"
              fontSize="sm"
              fontWeight="600"
            >
              Cancel
            </Button>

            <Button
              colorScheme="teal"
              h="45px"
              px={8}
              borderRadius="xl"
              isLoading={loading}
              onClick={handleSetProcess}
              boxShadow="0 10px 15px -3px rgba(45, 212, 191, 0.3)"
              _hover={{ boxShadow: "0 20px 25px -5px rgba(45, 212, 191, 0.4)" }}
              fontSize="sm"
              fontWeight="700"
            >
              Apply Changes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
